import type { QueryKey } from '@tanstack/react-query';

import type { ServiceCategoryRow } from '@/api/booking/getServiceCategoriesQuery';
import { Logger } from '@/utils/logging';
import type { LoggerContext } from '@/utils/logging/_types';

interface CategoryListDataErrorDetails {
  log: { level: 'error' | 'warn'; message: string; context: LoggerContext };
  /** Queries that succeeded with unusable data, so a retry must reset them. */
  staleQueryKeys: QueryKey[];
}

/**
 * Thrown by the category list when its requests succeeded but the data cannot
 * be listed (a cart id with no cart, or cart services missing from the shop
 * services). The error boundary logs it and resets its queries on retry.
 */
export class CategoryListDataError extends Error {
  readonly log: CategoryListDataErrorDetails['log'];

  readonly staleQueryKeys: QueryKey[];

  constructor(message: string, details: CategoryListDataErrorDetails) {
    super(message);
    this.name = 'CategoryListDataError';
    this.log = details.log;
    this.staleQueryKeys = details.staleQueryKeys;
  }
}

export const logCategoryListError = (error: unknown) => {
  if (!(error instanceof CategoryListDataError)) return;
  Logger[error.log.level](error.log.message, error.log.context);
};

interface AssertCartAvailableParams {
  shopId: string;
  cartId?: string;
  cart: unknown;
  cartQueryKey: QueryKey;
}

/**
 * getCartQuery returns null on a failed fetch, so with a cartId a null cart is
 * an error, not an empty cart.
 */
export function assertCartAvailable({
  shopId,
  cartId,
  cart,
  cartQueryKey,
}: AssertCartAvailableParams) {
  if (cartId && cart === null) {
    throw new CategoryListDataError('Cart unavailable', {
      log: {
        level: 'error',
        message: 'Cart unavailable on category screen',
        context: { shopId, cartId },
      },
      staleQueryKeys: [cartQueryKey],
    });
  }
}

interface AssertCartServicesAvailableParams {
  shopId: string;
  cartId?: string;
  /** Whether the list depends on the cart's services being in the response. */
  dependsOnServices: boolean;
  cartServiceIds: string[];
  /** The cart services found in the services response. */
  cartServices: { id: string }[] | undefined;
  cartQueryKey: QueryKey;
  servicesQueryKey: QueryKey;
}

/** Throws when a cart service is missing from the services response. */
export function assertCartServicesAvailable({
  shopId,
  cartId,
  dependsOnServices,
  cartServiceIds,
  cartServices,
  cartQueryKey,
  servicesQueryKey,
}: AssertCartServicesAvailableParams) {
  const missingServiceIds = cartServiceIds.filter(
    (id) => !cartServices?.some((service) => service.id === id),
  );
  if (dependsOnServices && missingServiceIds.length > 0) {
    throw new CategoryListDataError('Cart services missing', {
      log: {
        level: 'warn',
        message: 'Cart services missing from the shop services',
        context: { shopId, cartId, cartServiceIds, missingServiceIds },
      },
      staleQueryKeys: [cartQueryKey, servicesQueryKey],
    });
  }
}

/** Whether the row is a visible L1 category (no parent, not hidden). */
export const isVisibleL1Row = (
  row: Pick<ServiceCategoryRow, 'parentCategoryId' | 'visible'> | undefined,
): boolean => !!row && !row.parentCategoryId && row.visible !== false;

interface AssertGroupAvailableParams {
  shopId: string;
  groupId: string;
  /** The row with `groupId` in the categories response, if any. */
  group: Pick<ServiceCategoryRow, 'parentCategoryId' | 'visible'> | undefined;
  categoriesQueryKey: QueryKey;
}

/** Throws when the group id is not a visible L1 category. */
export function assertGroupAvailable({
  shopId,
  groupId,
  group,
  categoriesQueryKey,
}: AssertGroupAvailableParams) {
  if (!isVisibleL1Row(group)) {
    throw new CategoryListDataError('Category group unavailable', {
      log: {
        level: 'warn',
        message: 'Subcategory group is not a visible L1 category',
        context: { shopId, groupId },
      },
      staleQueryKeys: [categoriesQueryKey],
    });
  }
}
